import { useCallback } from 'react';
import { useModalContext, ModalConfig } from './ModalContext';
import { RootState, useAppSelector } from '../../store';

export const useModal = () => {
    const { openModal, closeModal, isOpen } = useModalContext();
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);

    const open = useCallback(
        (config: ModalConfig) => {
            openModal(config);
        },
        [openModal],
    );

    const confirm = useCallback(
        (options: {
            title: string;
            content: string | React.ReactNode;
            onConfirm: () => void;
            onCancel?: () => void;
            closeOnPrimaryClick?: boolean;
            closeOnSecondaryClick?: boolean;
            confirmText?: string;
            cancelText?: string;
            size?: 'small' | 'medium' | 'large' | 'full';
            confirmVariant?: 'primary' | 'secondary' | 'secondaryOutlined';
            cancelVariant?: 'primary' | 'secondary' | 'secondaryOutlined';
        }) => {
            openModal({
                title: options.title,
                content: options.content,
                size: options.size || 'small',
                showCloseButton: false,
                closeOnBackdropClick: false,
                primaryButton: {
                    text: options.confirmText || i18n.translate({ id: 'confirmDeleteChat.button', sourceName: 'Main' }),
                    onClick: options.onConfirm,
                    variant: options.confirmVariant || 'primary',
                    closeOnClick: options.closeOnPrimaryClick ?? true,
                },
                secondaryButton: {
                    text: options.cancelText || i18n.translate({ id: 'closeConfirmDialog.button', sourceName: 'Main' }),
                    onClick: options.onCancel || (() => {}),
                    variant: options.cancelVariant || 'secondary',
                    closeOnClick: options.closeOnSecondaryClick ?? true,
                },
            });
        },
        [openModal, i18n],
    );

    const deleteConfirm = useCallback(
        (options: {
            title: string;
            content: string | React.ReactNode;
            onConfirm: () => void;
            onCancel?: () => void;
            confirmText?: string;
            cancelText?: string;
            size?: 'small' | 'medium' | 'large' | 'full';
            confirmVariant?: 'primary' | 'secondary' | 'secondaryOutlined';
            cancelVariant?: 'primary' | 'secondary' | 'secondaryOutlined';
            closeOnPrimaryClick?: boolean;
            closeOnSecondaryClick?: boolean;
        }) => {
            openModal({
                title: options.title,
                content: options.content,
                size: options.size || 'small',
                showCloseButton: false,
                closeOnBackdropClick: false,
                primaryButton: {
                    text: options.confirmText || i18n.translate({ id: 'confirm.default.delete', sourceName: 'Modal' }),
                    onClick: options.onConfirm,
                    variant: options.confirmVariant || 'primary',
                    closeOnClick: options.closeOnPrimaryClick ?? true,
                },
                secondaryButton: {
                    text: options.cancelText || i18n.translate({ id: 'confirm.default.cancel', sourceName: 'Modal' }),
                    onClick: options.onCancel || (() => {}),
                    variant: options.cancelVariant || 'secondary',
                    closeOnClick: options.closeOnSecondaryClick ?? true,
                },
            });
        },
        [openModal, i18n],
    );

    const alert = useCallback(
        (options: {
            title: string;
            content: string | React.ReactNode;
            onClose?: () => void;
            buttonText?: string;
            size?: 'small' | 'medium' | 'large' | 'full';
            shouldScrollToBottom?: boolean;
            onContentRendered?: () => void;
            closeOnClick?: boolean;
        }) => {
            openModal({
                title: options.title,
                content: options.content,
                size: options.size || 'small',
                showCloseButton: false,
                shouldScrollToBottom: options.shouldScrollToBottom,
                onContentRendered: options.onContentRendered,
                primaryButton: {
                    text: options.buttonText || 'OK',
                    onClick: options.onClose || (() => {}),
                    closeOnClick: options.closeOnClick ?? true,
                },
            });
        },
        [openModal],
    );

    const optionReset = useCallback(
        (options: {
            title: string;
            content: string | React.ReactNode;
            onClose?: () => void;
            onConfirm?: () => void;
            buttonText?: string;
            size?: 'small' | 'medium' | 'large' | 'full';
        }) => {
            openModal({
                title: options.title,
                content: options.content,
                size: options.size || 'small',
                showCloseButton: false,
                primaryButton: {
                    text:
                        options.buttonText || i18n.translate({ id: 'resetSystemPrompt.confirm', sourceName: 'Prompt' }),
                    onClick: options.onConfirm || (() => {}),
                    variant: 'primary',
                },
                secondaryButton: {
                    text: i18n.translate({ id: 'closeConfirmDialog.button', sourceName: 'Main' }),
                    onClick: options.onClose || (() => {}),
                    variant: 'secondary',
                },
            });
        },
        [openModal, i18n],
    );

    const openWithButtons = useCallback(
        (options: {
            title: string;
            content: string | React.ReactNode;
            buttons?: Array<{
                text: string;
                onClick: () => void;
                variant?: 'primary' | 'secondary' | 'secondaryOutlined' | 'secondaryRound' | 'primaryRound';
                key?: string;
            }>;
            customButtons?: React.ReactNode[];
            size?: 'small' | 'medium' | 'large' | 'full';
            showCloseButton?: boolean;
            closeOnBackdropClick?: boolean;
            onClose?: () => void;
        }) => {
            openModal({
                title: options.title,
                content: options.content,
                size: options.size || 'small',
                showCloseButton: options.showCloseButton !== undefined ? options.showCloseButton : true,
                closeOnBackdropClick: options.closeOnBackdropClick !== undefined ? options.closeOnBackdropClick : true,
                buttonConfigs: options.buttons,
                customButtons: options.customButtons,
                onClose: options.onClose,
            });
        },
        [openModal],
    );

    return {
        open,
        openWithButtons,
        confirm,
        deleteConfirm,
        alert,
        close: closeModal,
        optionReset,
        isOpen,
    };
};

export default useModal;
